import z from "zod";
import { memo, useEffect } from "react";
import { usePathAccountName } from "../../../hooks/router";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import { accountDashboardLayoutRoute, accountRoutes } from "../../../App";
import { createRoute, Outlet } from "@tanstack/react-router";
import { TilesetOverview } from "./Overview";
import { TilesetMeta } from "./Metadata";
import { TilesetUsage } from "./Usage";
import { TilesetVersions } from "./Versions";
import { TilesetConfig } from "./Config";
import { MenuType, type NavMenuOptions } from "../../../types/menu";
import { memoize } from "es-toolkit";
import { get } from "es-toolkit/compat";
import { IconArrowLeft, IconChartInfographic, IconFileInfo, IconHome, IconSettings, IconVersions } from "@tabler/icons-react";
import { NewTileset } from "./NewTileset";
import { Tilesets } from "./Tilesets";
import { useSessionAccountName } from "../../../hooks/user";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "../../../trpc";
import { DashboardLayout } from "../../../layouts/DashboardLayout";

const tilesetNav = memoize(({
  tilesetAccountName = "",
  currentAccountName,
  tileset,
  dataset,
  intl,
}: {
  tilesetAccountName?: string; currentAccountName?: string; tileset: string; dataset?: string; intl: IntlShape; }): NavMenuOptions => ({
  items: [
    {
      title: intl.formatMessage({
        defaultMessage: "Back",
        id: "navigation.back",
      }),
      type: MenuType.Single,
      Icon: IconArrowLeft,
      to: "/$accountName/tilesets",
      params: { accountName: currentAccountName ?? tilesetAccountName },
    },
    {
      type: MenuType.Divider,
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Overview",
        id: "navigation.overview",
      }),
      type: MenuType.Single,
      Icon: IconHome,
      to: "/$accountName/tilesets/$tileset/overview",
      params: { tileset, accountName: tilesetAccountName },
      search: dataset ? { dataset } : undefined,
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Metadata",
        id: "navigation.metadata",
      }),
      type: MenuType.Single,
      Icon: IconFileInfo,
      to: "/$accountName/tilesets/$tileset/metadata",
      params: { tileset, accountName: tilesetAccountName },
      search: dataset ? { dataset } : undefined,
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Usage",
        id: "navigation.usage",
      }),
      type: MenuType.Single,
      Icon: IconChartInfographic,
      to: "/$accountName/tilesets/$tileset/usage",
      params: { tileset, accountName: tilesetAccountName },
      search: dataset ? { dataset } : undefined,
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Versions",
        id: "navigation.versions",
      }),
      type: MenuType.Single,
      Icon: IconVersions,
      to: "/$accountName/tilesets/$tileset/versions",
      params: { tileset, accountName: tilesetAccountName },
      search: dataset ? { dataset } : undefined,
    },
    {
      title: intl.formatMessage({
        defaultMessage: "Config",
        id: "navigation.config",
      }),
      type: MenuType.Single,
      Icon: IconSettings,
      to: "/$accountName/tilesets/$tileset/config",
      params: { tileset, accountName: tilesetAccountName },
      search: dataset ? { dataset } : undefined,
    },
  ],
}), { getCacheKey: ({ tileset, dataset, tilesetAccountName, currentAccountName, intl }) => `${dataset}-${tileset}-${tilesetAccountName}-${currentAccountName}-${intl.locale}` });

export const tilesetDashboardLayoutRoute = createRoute({
  getParentRoute: () => accountRoutes,
  path: "/tilesets/$tileset",
  validateSearch: z.object({
    dataset: z.string().optional(),
  }),
  component: memo(() => {
    const intl = useIntl();
    const trpc = useTRPC();
    const tilesetAccountName = usePathAccountName();
    const currentAccountName = useSessionAccountName();

    const { tileset } = tilesetDashboardLayoutRoute.useParams();
    const { dataset } = tilesetDashboardLayoutRoute.useSearch();

    const menu = tilesetNav({
      tileset,
      dataset,
      tilesetAccountName,
      currentAccountName,
      intl,
    });

    const { mutateAsync: createUserActivity } = useMutation(trpc.createUserActivity.mutationOptions());
    const { data: tilesetWithVersions } = useQuery(trpc.getTilesetWithVersions.queryOptions({
      tileset,
      dataset,
      account: tilesetAccountName,
    }, {
      refetchInterval: 1000 * 60 * 5,
      refetchOnMount: false,
    }));

    useEffect(() => {
      if (!tilesetWithVersions) return;
      createUserActivity({
        type: "tileset",
        objectId: tilesetWithVersions.tileset.id,
      }).catch((error: Error) => {
        logger.error({ error }, "Failed to create tileset user activity");
      });
    }, [get(tilesetWithVersions, "tileset.id")]);

    return (
      <DashboardLayout menu={menu}>
        <Outlet />
      </DashboardLayout>
    );
  }),
});

const tilesetOverviewRoute = createRoute({
  getParentRoute: () => tilesetDashboardLayoutRoute,
  path: "/overview",
  component: TilesetOverview,
});

const tilesetMetadataRoute = createRoute({
  getParentRoute: () => tilesetDashboardLayoutRoute,
  path: "/metadata",
  component: TilesetMeta,
  validateSearch: z.object({
    dataset: z.string().optional(),
  }),
});

const tilesetUsageRoute = createRoute({
  getParentRoute: () => tilesetDashboardLayoutRoute,
  path: "/usage",
  component: TilesetUsage,
});

const tilesetVersionsRoute = createRoute({
  getParentRoute: () => tilesetDashboardLayoutRoute,
  path: "/versions",
  component: TilesetVersions,
  validateSearch: z.object({
    dataset: z.string().optional(),
  }),
});

const tilesetConfigRoute = createRoute({
  getParentRoute: () => tilesetDashboardLayoutRoute,
  path: "/config",
  component: TilesetConfig,
});

export const accountNewTilesetRoute = createRoute({
  getParentRoute: () => accountDashboardLayoutRoute,
  path: "/new-tileset",
  component: NewTileset,
});

export const accountTilesetsListRoute = createRoute({
  getParentRoute: () => accountDashboardLayoutRoute,
  path: "/tilesets",
  component: Tilesets,
});

export const accountTilesetRoutes = tilesetDashboardLayoutRoute.addChildren([
  tilesetOverviewRoute,
  tilesetMetadataRoute,
  tilesetUsageRoute,
  tilesetVersionsRoute,
  tilesetConfigRoute,
]);
